import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Heart, 
  Sparkles, 
  Users, 
  Music,
  UtensilsCrossed,
  Lightbulb,
  Car,
  Check,
  ArrowRight,
  Phone,
  Trees,
  Martini,
  Beef,
  Baby,
  Cake,
  Briefcase
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { CONTACT } from '../lib/siteConfig';

const GARDEN_HERO = "/images/jardin/03.jpg";
const WEDDING_IMAGE = "/images/jardin/05.jpg";

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const eventTypes = [
  { 
    icon: Heart, 
    title: 'Bodas', 
    desc: 'Celebra el comienzo de una nueva historia en un entorno rodeado de naturaleza. Los jardines de La Finca crean un ambiente íntimo y especial para disfrutar de tu ceremonia y recepción junto a las personas que más quieres.',
    color: 'bg-pink-50 text-pink-600'
  },
  { 
    icon: Sparkles, 
    title: 'XV Años', 
    desc: 'Celebra sus XV años en un entorno lleno de naturaleza y momentos inolvidables. Un espacio especial para reunir a familiares y amigos, disfrutar de la celebración y hacer de este gran día un recuerdo para toda la vida.',
    color: 'bg-purple-50 text-purple-600'
  },
  { 
    icon: Baby,
    title: 'Bautizos',
    desc: 'Celebra este momento tan especial rodeado de naturaleza y de las personas que más quieres. La Finca ofrece un ambiente tranquilo y acogedor, ideal para compartir en familia y crear un hermoso recuerdo de este día.',
    color: 'bg-blue-50 text-blue-600'
  },
  { 
    icon: Cake,
    title: 'Cumpleaños',
    desc: 'Celebra un año más rodeado de las personas que hacen especial tu vida. Disfruta de un ambiente privado, jardines y espacios ideales para convivir, celebrar y crear recuerdos inolvidables.',
    color: 'bg-amber-50 text-amber-600'
  },
  { 
    icon: Briefcase,
    title: 'Eventos Corporativos',
    desc: 'Un entorno diferente para conectar, compartir y fortalecer a tu equipo. La Finca ofrece un espacio privado y rodeado de naturaleza, ideal para reuniones, convivencias, capacitaciones y celebraciones empresariales.',
    color: 'bg-emerald-50 text-emerald-600'
  },
];

const features = [
  { icon: Users, label: 'Hasta 150 invitados ' },
  { icon: Beef, label: 'Área de asador y cocina ' },
  { icon: Car, label: 'Estacionamiento' },
  { icon: Lightbulb, label: 'Iluminación cálida' },
  { icon: Check, label: 'Baños separados' },
];

const packages = [
  {
    name: 'Paquete Mobiliario Básico',
    features: [
      '6 horas de evento.',
      'El uso de las instalaciones con alberca y jacuzzi climatizado (no incluye hospedarse en la Villa)',
      'Refrigerador, hielera y bocina de sonido USB/Bluetooth.',
      'Iluminación cálida decorativa en zonas especiales de la propiedad.',
    ],
    cta: 'Ideal para celebraciones sencillas y momentos especiales con tus invitados.'
  },
  {
    name: 'Paquete Mobiliario Madera',
    highlight: true,
    features: [
      '6 horas de evento.',
      'El uso de las instalaciones con alberca y jacuzzi climatizado (no incluye hospedarse en la Villa)',
      'Mobiliario de madera: Mesas rectangular y redonda con sillas crossback.',
      'Mesa principal blanca de madera con sillas tapizadas ',
      'Refrigerador, hielera y bocina de sonido USB/Bluetooth.',
      'Iluminación cálida decorativa en zonas especiales de la propiedad.',
    ],
    cta: 'Ideal para celebrar tu evento más especial. '
  },
  {
    name: 'Paquete Evento y hospedaje ',
    features: [
      'Noche de hospedaje (Check-in 2:00pm Check-out 11:00am)',
      '⁠El uso de las instalaciones con alberca y jacuzzi climatizado',
      'Mobiliario básico o de madera  ',
      'Refrigerador, hielera y bocina de sonido USB/Bluetooth.',
      'Iluminación cálida decorativa en zonas especiales de la propiedad.',
    ],
    cta: 'Ideal para celebrar tu evento sin límite '
  },
];

export default function GardenPage() {
  return (
    <div data-testid="garden-page" className="pt-20">
      {/* Hero */}
      <section className="relative h-[70vh] min-h-[500px]">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${GARDEN_HERO})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-black/20" />
        </div>

        <div className="relative h-full flex flex-col justify-end pb-16 px-4">
          <div className="max-w-7xl mx-auto w-full">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <p className="font-serif-alt italic text-[#C8962D] text-lg mb-2">
                Celebra con Nosotros
              </p>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white mb-4">
                Jardín de Eventos
              </h1>
              <p className="text-white/80 text-lg max-w-2xl">
                Un espacio rodeado de jardines y naturaleza, creado para celebrar los momentos que más importan. Disfruta de un ambiente privado, acogedor y especial para compartir con tus invitados y crear recuerdos inolvidables.<br></br><br></br>
                Capacidad para eventos de hasta 150 personas.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="bg-[#3D5A3E] py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { icon: Users, value: '150', label: 'Invitados' },
              { icon: Trees, value: '1,050', label: 'm² de jardín' },
              { icon: Martini, value: '1', label: 'Cantina' },
              { icon: Beef, value: '1', label: 'Parrilla y Asador' },
            ].map((stat) => (
              <div key={stat.label} className="flex flex-col items-center">
                <stat.icon className="h-7 w-7 text-[#C8962D] mb-2" />
                <p className="font-serif text-3xl md:text-4xl text-white">{stat.value}</p>
                <p className="text-white/70 text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Event Types */}
      <section className="py-24 bg-[#FAF7F2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="font-serif-alt italic text-[#C8962D] text-lg mb-2">
              Para toda ocasión
            </p>
            <h2 className="font-serif text-3xl md:text-4xl text-[#1A1A1A]">
              Tipos de Eventos
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {eventTypes.map((event, index) => (
              <motion.div
                key={event.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-shadow"
              >
                <div className={`w-14 h-14 rounded-xl ${event.color} flex items-center justify-center mb-4`}>
                  <event.icon className="h-7 w-7" />
                </div>
                <h3 className="font-serif text-2xl text-[#1A1A1A] mb-3">{event.title}</h3>
                <p className="text-[#1A1A1A]/70 leading-relaxed">{event.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <h2 className="font-serif text-3xl md:text-4xl text-[#1A1A1A] mb-6">
                Todo para Tu Evento
              </h2>
              <p className="text-[#1A1A1A]/70 text-lg leading-relaxed mb-8">
                Espacios pensados para que disfrutes tu celebración de principio a fin. La Finca cuenta con jardín, área techada, terraza, barra, asador, alberca y jacuzzi climatizados, baños y estacionamiento, creando un entorno cómodo y versátil para recibir a tus invitados y hacer de cada evento un momento especial.
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {features.map((feature) => (
                  <div
                    key={feature.label}
                    className="flex items-center space-x-3 p-3 bg-[#FAF7F2] rounded-lg"
                  >
                    <feature.icon className="h-5 w-5 text-[#C8962D]" />
                    <span className="text-sm text-[#1A1A1A]">{feature.label}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="overflow-hidden rounded-2xl">
                <img
                  src={WEDDING_IMAGE}
                  alt="Wedding Setup"
                  className="w-full h-[500px] object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-[#5C4033] text-white p-6 rounded-xl">
                <p className="font-serif text-2xl">Visita La Finca</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Packages */}
      <section className="py-24 bg-[#FAF7F2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="font-serif-alt italic text-[#C8962D] text-lg mb-2">
              Opciones flexibles
            </p>
            <h2 className="font-serif text-3xl md:text-4xl text-[#1A1A1A]">
              Paquetes de Eventos
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {packages.map((pkg, index) => (
              <motion.div
                key={pkg.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`rounded-2xl p-8 ${
                  pkg.highlight
                    ? 'bg-[#5C4033] text-white ring-4 ring-[#C8962D] scale-105'
                    : 'bg-white'
                }`}
              >
                {pkg.highlight && (
                  <span className="inline-block bg-[#C8962D] text-white text-xs px-3 py-1 rounded-full mb-4">
                    Más Popular
                  </span>
                )}
                <h3 className={`font-serif text-2xl mb-6 ${pkg.highlight ? 'text-white' : 'text-[#1A1A1A]'}`}>
                  {pkg.name}
                </h3>
                <ul className="space-y-3 mb-8">
                  {pkg.features.map((feature) => (
                    <li key={feature} className="flex items-start">
                      <Check className={`h-5 w-5 mr-3 flex-shrink-0 ${pkg.highlight ? 'text-[#C8962D]' : 'text-[#3D5A3E]'}`} />
                      <span className={pkg.highlight ? 'text-white/90' : 'text-[#1A1A1A]/70'}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
                <p className={`text-sm mb-6 ${pkg.highlight ? 'text-white/70' : 'text-[#1A1A1A]/60'}`}>
                  {pkg.cta}
                </p>
                <Link to="/contacto">
                  <Button
                    className={`w-full ${
                      pkg.highlight
                        ? 'bg-[#C8962D] hover:bg-[#B8861D] text-white'
                        : 'bg-[#5C4033] hover:bg-[#3D5A3E] text-white'
                    }`}
                  >
                    Solicitar Cotización
                  </Button>
                </Link>
              </motion.div>
            ))}
          </div>

          <p className="text-center text-[#1A1A1A]/60 mt-8">
            * Los precios varían según fecha y requerimientos específicos. Contáctanos para una cotización personalizada.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#3D5A3E]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-6">
            ¿Tienes un evento en mente?
          </h2>
          <p className="text-white/80 text-lg mb-8">
            Platícanos sobre tu celebración y te ayudaremos a hacerla realidad
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contacto">
              <Button
                data-testid="garden-contact-btn"
                size="lg"
                className="bg-[#C8962D] hover:bg-[#B8861D] text-white px-12"
              >
                Enviar Consulta
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <a href={CONTACT.phoneHref}>
              <Button
                variant="outline"
                size="lg"
                className="border-white text-white hover:bg-white/10 px-12"
              >
                <Phone className="mr-2 h-5 w-5" />
                Llamar Ahora
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
